import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, BtnSecondary } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// Defined outside the component so React doesn't unmount/remount the input on
// every keystroke (caused focus loss — see bugs-fixed-batch-1 in memory).
const FormField = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 8,
  fontSize: 13,
  color: S.textPrimary,
  outline: 'none',
  boxSizing: 'border-box',
};

const empty = {
  type: 'individual',
  firstName: '', lastName: '', dateOfBirth: '', gender: '',
  companyName: '', companyRegistration: '', contactPerson: '',
  email: '', phone: '',
  address: { street: '', city: '', state: '', postalCode: '', country: 'CH' },
  externalId: '', tags: '', notes: '', status: 'active',
};

const NewClientPanel = ({ onClose, onCreated, client }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(client && client._id);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (client && client._id) {
      setForm({
        ...empty,
        ...client,
        address: { ...empty.address, ...(client.address || {}) },
        dateOfBirth: client.dateOfBirth ? String(client.dateOfBirth).slice(0, 10) : '',
        tags: Array.isArray(client.tags) ? client.tags.join(', ') : (client.tags || ''),
      });
    } else {
      setForm(empty);
    }
  }, [client]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setAddr = (key, value) => setForm(prev => ({ ...prev, address: { ...prev.address, [key]: value } }));

  const handleSave = async () => {
    setError('');
    if (form.type === 'individual') {
      if (!form.firstName?.trim() || !form.lastName?.trim()) {
        setError(t('clientsModule.new.errFirstLast'));
        return;
      }
    }
    if (form.type === 'company' && !form.companyName?.trim()) {
      setError(t('clientsModule.new.errCompany'));
      return;
    }
    // D-10: email field previously used <input type='email'> only. The
    // browser validator only kicks in when the input is inside a
    // <form> with submit — this panel uses button.onClick so the
    // native check never fires and "abc" saved through. Add an
    // explicit regex check (matches validators.js validateEmail
    // pattern) so short/malformed emails bounce back with a message
    // instead of silently persisting.
    const email = String(form.email || '').trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setError(t('clientsModule.new.errEmail', { defaultValue: 'Please enter a valid email address.' }));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: typeof form.tags === 'string'
          ? form.tags.split(',').map(t => t.trim()).filter(Boolean)
          : (form.tags || []),
        dateOfBirth: form.dateOfBirth || undefined,
        gender: form.gender || null,
      };
      if (isEdit) {
        await axios.put(`${BASE_URL}/clients/${client._id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BASE_URL}/clients`, payload, { withCredentials: true });
      }
      onCreated && onCreated();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || t('clientsModule.new.errSave'));
    } finally {
      setSaving(false);
    }
  };

  const isIndividual = form.type === 'individual';

  return (
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
    >
      <div
        style={{ width: 520, height: '100%', background: '#fff', boxShadow: '-4px 0 32px rgba(0,0,0,0.15)', overflowY: 'auto', padding: 28 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>{isEdit ? t('clientsModule.new.titleEdit') : t('clientsModule.new.titleNew')}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name='x' color={S.textMuted} size={20} />
          </button>
        </div>

        {/* Type toggle */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 18, background: S.bgCard, borderRadius: 8, padding: 4, border: `1px solid ${S.borderLight}` }}>
          {['individual', 'company'].map(opt => (
            <button
              key={opt}
              onClick={() => set('type', opt)}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: form.type === opt ? '#fff' : 'transparent',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                color: form.type === opt ? S.burgundy : S.textMuted,
                cursor: 'pointer',
                boxShadow: form.type === opt ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {opt === 'individual' ? t('clientsModule.new.typeIndividual') : t('clientsModule.new.typeCompany')}
            </button>
          ))}
        </div>

        {isIndividual ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label={t('clientsModule.new.firstName')}>
                <input value={form.firstName} onChange={e => set('firstName', e.target.value)} style={inputStyle} />
              </FormField>
              <FormField label={t('clientsModule.new.lastName')}>
                <input value={form.lastName} onChange={e => set('lastName', e.target.value)} style={inputStyle} />
              </FormField>
              <FormField label={t('clientsModule.new.dateOfBirth')}>
                <input type='date' value={form.dateOfBirth || ''} onChange={e => set('dateOfBirth', e.target.value)} style={inputStyle} />
              </FormField>
              <FormField label={t('clientsModule.new.gender')}>
                <select value={form.gender || ''} onChange={e => set('gender', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value=''>{t('clientsModule.new.genderNone')}</option>
                  <option value='M'>{t('clientsModule.new.genderMale')}</option>
                  <option value='F'>{t('clientsModule.new.genderFemale')}</option>
                </select>
              </FormField>
            </div>
          </>
        ) : (
          <>
            <FormField label={t('clientsModule.new.companyName')}>
              <input value={form.companyName} onChange={e => set('companyName', e.target.value)} style={inputStyle} />
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label={t('clientsModule.new.registrationNumber')}>
                <input value={form.companyRegistration} onChange={e => set('companyRegistration', e.target.value)} style={inputStyle} />
              </FormField>
              <FormField label={t('clientsModule.new.contactPerson')}>
                <input value={form.contactPerson} onChange={e => set('contactPerson', e.target.value)} style={inputStyle} />
              </FormField>
            </div>
          </>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormField label={t('clientsModule.new.email')}>
            <input type='email' value={form.email} onChange={e => set('email', e.target.value)} style={inputStyle} />
          </FormField>
          <FormField label={t('clientsModule.new.phone')}>
            <input value={form.phone} onChange={e => set('phone', e.target.value)} style={inputStyle} />
          </FormField>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 6, marginBottom: 10 }}>{t('clientsModule.new.addressSection')}</div>
        <FormField label={t('clientsModule.new.street')}>
          <input value={form.address.street} onChange={e => setAddr('street', e.target.value)} style={inputStyle} />
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <FormField label={t('clientsModule.new.city')}>
            <input value={form.address.city} onChange={e => setAddr('city', e.target.value)} style={inputStyle} />
          </FormField>
          <FormField label={t('clientsModule.new.state')}>
            <input value={form.address.state} onChange={e => setAddr('state', e.target.value)} style={inputStyle} />
          </FormField>
          <FormField label={t('clientsModule.new.postalCode')}>
            <input value={form.address.postalCode} onChange={e => setAddr('postalCode', e.target.value)} style={inputStyle} />
          </FormField>
        </div>
        <FormField label={t('clientsModule.new.country')}>
          <select value={form.address.country} onChange={e => setAddr('country', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value='CH'>{t('clientsModule.new.countryCH')}</option>
            <option value='US'>{t('clientsModule.new.countryUS')}</option>
            <option value='FR'>{t('clientsModule.new.countryFR')}</option>
            <option value='DE'>{t('clientsModule.new.countryDE')}</option>
            <option value='IT'>{t('clientsModule.new.countryIT')}</option>
            <option value='AT'>{t('clientsModule.new.countryAT')}</option>
            <option value='LI'>{t('clientsModule.new.countryLI')}</option>
          </select>
        </FormField>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormField label={t('clientsModule.new.externalId')}>
            <input value={form.externalId} onChange={e => set('externalId', e.target.value)} placeholder={t('clientsModule.new.externalIdPh')} style={inputStyle} />
          </FormField>
          <FormField label={t('clientsModule.new.status')}>
            <select value={form.status} onChange={e => set('status', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value='active'>{t('clientsModule.new.statusActive')}</option>
              <option value='prospect'>{t('clientsModule.new.statusProspect')}</option>
              <option value='inactive'>{t('clientsModule.new.statusInactive')}</option>
            </select>
          </FormField>
        </div>
        <FormField label={t('clientsModule.new.tags')}>
          <input value={form.tags} onChange={e => set('tags', e.target.value)} placeholder={t('clientsModule.new.tagsPh')} style={inputStyle} />
        </FormField>
        <FormField label={t('clientsModule.new.notes')}>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </FormField>

        {error && (
          <div style={{ padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <BtnSecondary onClick={onClose} style={{ flex: 1 }}>{t('clientsModule.new.cancel')}</BtnSecondary>
          <BtnPrimary onClick={handleSave} disabled={saving} style={{ flex: 1 }}>
            {saving ? t('clientsModule.new.saving') : isEdit ? t('clientsModule.new.updateClient') : t('clientsModule.new.createClient')}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
};

export default NewClientPanel;
